import React from "react";
import { Lock } from "lucide-react";

export const VOID_REASON_SEPARATOR = "\n\nVoid Reason: ";

export function splitManagedVoidResolution(resolution, managed = false) {
  const text = String(resolution || "");
  if (!managed) return { baseResolution: text, reason: "", suffix: "" };
  const separatorIndex = text.lastIndexOf(VOID_REASON_SEPARATOR);
  if (separatorIndex < 0) return { baseResolution: text, reason: "", suffix: "" };
  const reason = text.slice(separatorIndex + VOID_REASON_SEPARATOR.length);
  if (!reason.trim() || /[\r\n]/.test(reason)) {
    return { baseResolution: text, reason: "", suffix: "" };
  }
  return {
    baseResolution: text.slice(0, separatorIndex),
    reason,
    suffix: text.slice(separatorIndex),
  };
}

export function preserveManagedVoidResolution(resolution, nextBaseResolution, managed = false) {
  const { suffix } = splitManagedVoidResolution(resolution, managed);
  return `${nextBaseResolution}${suffix}`;
}

export function normalizeManagedVoidResolution(resolution, managed = false) {
  const { baseResolution, suffix } = splitManagedVoidResolution(resolution, managed);
  return `${baseResolution.trim()}${suffix}`;
}

export default function ProtectedResolutionField({
  id,
  value,
  onChange,
  persistedVoid = false,
  showLockedSuffix = false,
  draftReason = "",
  disabled = false,
  placeholder,
  textareaClassName = "",
  containerClassName = "",
}) {
  const parsed = splitManagedVoidResolution(value, persistedVoid);
  const displayedReason = draftReason || parsed.reason;
  const protectedDisplay = showLockedSuffix && Boolean(displayedReason);
  const textarea = (
    <textarea
      id={id}
      value={parsed.baseResolution}
      onChange={(event) =>
        onChange?.(
          preserveManagedVoidResolution(value, event.target.value, persistedVoid)
        )
      }
      disabled={disabled}
      placeholder={placeholder}
      className={textareaClassName}
      data-testid={`${id}-base`}
    />
  );

  if (!protectedDisplay) return textarea;

  return (
    <div
      className={`protected-resolution-field mt-2 flex min-w-0 flex-col overflow-hidden rounded-[calc(var(--radius)-0.25rem)] border border-input bg-card transition-colors focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/15 ${containerClassName}`}
      data-testid={`${id}-container`}
    >
      {React.cloneElement(textarea, {
        className: "protected-resolution-field__textarea min-h-0 w-full min-w-0 flex-1 resize-none appearance-none overflow-y-auto rounded-none border-0 bg-transparent px-3 py-2.5 text-sm text-foreground shadow-none outline-none ring-0 placeholder:text-muted-foreground focus:border-transparent focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-45",
      })}
      <div
        className="flex max-h-12 min-w-0 flex-none select-none items-start gap-2 overflow-y-auto border-t border-border px-3 py-2.5 text-sm text-muted-foreground"
        aria-readonly="true"
        data-testid={`${id}-locked-suffix`}
      >
        <Lock className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.8} aria-hidden="true" />
        <span className="min-w-0 whitespace-pre-wrap break-words">Void Reason: {displayedReason}</span>
      </div>
    </div>
  );
}
